<template>
  <!--begin::Content wrapper-->
  <div class="d-flex flex-column flex-column-fluid">
    <!--begin::Toolbar-->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <!--begin::Page title-->
        <div
          class="page-title d-flex flex-column justify-content-center flex-wrap me-3"
        >
          <h1
            class="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0"
          >
            {{ $t("common.app_name") }}
          </h1>
          <ul
            class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1"
          >
            <li class="breadcrumb-item text-muted">
              <router-link to="/home" class="text-muted text-hover-primary">
                {{ $t("common.home") }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              {{ $t("account_deletion.breadcrumb") }}
            </li>
          </ul>
        </div>
        <!--end::Page title-->
      </div>
    </div>
    <!--end::Toolbar-->

    <!--begin::Content-->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <div class="row g-5 g-xl-10 mb-5 mb-xl-10" id="section-content">
          <div class="col-12">
            <div class="card shadow-lg border-0 rounded-4 h-100 bg-light">
              <div class="card-body p-5">
                <!-- Header with Logo and Icon -->
                <div class="text-center mb-5">
                  <div
                    class="mb-0 mb-lg-2 custom-logo"
                    style="text-align: center"
                  >
                    <img
                      alt="Logo"
                      src="/assets/img/logo-dark.png"
                      class="h-60px h-lg-75px"
                    />
                  </div>
                  <div
                    class="d-inline-flex align-items-center justify-content-center bg-success bg-opacity-10 rounded-circle p-4 shadow-sm"
                  >
                    <i
                      class="fas fa-user-slash fa-1x text-success"
                      style="font-size: 18px"
                    ></i>
                  </div>
                  <h2 style="font-weight: 700" class="fw-bold mt-3 text-dark">
                    {{ $t("account_deletion.page_title") }}
                  </h2>
                  <p class="text-muted">
                    {{ $t("account_deletion.effective_date") }}: 21/04/2025
                  </p>
                </div>

                <!-- Content Section -->
                <div class="policy-content">
                  <!-- Warning Box Before Deletion Policy -->
                  <div
                    class="alert alert-warning d-flex align-items-center mb-4"
                    role="alert"
                  >
                    <i class="fas fa-exclamation-triangle warning-icon"></i>
                    <div>
                      <strong>{{
                        $t("account_deletion.warning.title")
                      }}</strong>
                      <p class="mb-0 mt-1">
                        {{ $t("account_deletion.warning.text") }}
                      </p>
                    </div>
                  </div>

                  <!-- Account Deletion Policy Section -->
                  <PolicySection
                    :title="$t('account_deletion.deletion_policy.title')"
                    :icon="'fas fa-user-minus'"
                    :highlight="true"
                  >
                    <p class="text-dark">
                      {{ $t("account_deletion.deletion_policy.intro") }}
                    </p>
                    <ul>
                      <li
                        v-for="(item, index) in $tm(
                          'account_deletion.deletion_policy.items'
                        )"
                        :key="index"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </PolicySection>

                  <!-- What Happens After Deletion Section -->
                  <PolicySection
                    :title="$t('account_deletion.after_deletion.title')"
                    :icon="'fas fa-trash-alt'"
                  >
                    <p class="text-dark">
                      {{ $t("account_deletion.after_deletion.intro") }}
                    </p>
                    <ul>
                      <li
                        v-for="(item, index) in $tm(
                          'account_deletion.after_deletion.items'
                        )"
                        :key="index"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </PolicySection>

                  <!-- Data Retention & Privacy Section -->
                  <PolicySection
                    :title="$t('account_deletion.data_privacy.title')"
                    :icon="'fas fa-shield-alt'"
                  >
                    <p class="text-dark">
                      {{ $t("account_deletion.data_privacy.intro") }}
                    </p>
                    <ul>
                      <li
                        v-for="(item, index) in $tm(
                          'account_deletion.data_privacy.items'
                        )"
                        :key="index"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </PolicySection>
                </div>
                <!-- end policy content -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--end::Content-->
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import PolicySection from "./components/PolicySection.vue";

export default {
  name: "AccountDeletion",
  components: {
    PolicySection,
  },
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  mounted() {
    this.updatePageTitle();
  },
  watch: {
    "$i18n.locale"() {
      this.updatePageTitle();
    },
  },
  methods: {
    updatePageTitle() {
      document.title =
        this.$t("account_deletion.page_title") +
        " | " +
        this.$t("common.app_name");

      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute(
          "content",
          this.$t("account_deletion.page_title")
        );
      }
    },
  },
};
</script>

<style scoped>
.policy-content {
  line-height: 1.8;
  font-size: 15px;
  color: #333;
  max-width: 900px;
  margin: 0 auto;
}

.policy-content ul {
  padding-inline-start: 20px;
}

.policy-content ul li {
  margin-bottom: 8px;
}

.alert-warning {
  background: linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%);
  border: none;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 4px 16px rgba(255, 193, 7, 0.15);
  position: relative;
  overflow: hidden;
}

.alert-warning::before {
  content: "";
  position: absolute;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #ffc107 0%, #ffca2c 100%);
  border-radius: 16px 0 0 16px;
}

[dir="rtl"] .alert-warning::before {
  right: 0;
  left: auto;
}

[dir="ltr"] .alert-warning::before {
  left: 0;
  right: auto;
}

.warning-icon {
  color: #d39e00;
  font-size: 28px;
  margin-inline-end: 12px;
}

.alert-warning strong {
  color: #856404;
  font-size: 16px;
}

.alert-warning p {
  color: #6d5a00;
}

.card {
  border: none !important;
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%) !important;
}

.custom-logo img {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
}

.bg-success.bg-opacity-10 {
  background: linear-gradient(
    135deg,
    rgba(0, 168, 28, 0.1) 0%,
    rgba(0, 201, 34, 0.15) 100%
  ) !important;
}

h2.fw-bold {
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.contact-btn {
  font-size: 13px !important;
  padding: 10px 16px !important;
}

.contact-btn .btn-icon {
  font-size: 16px;
  margin-inline-end: 8px;
}

.rounded-pill.btn-success {
  background: linear-gradient(135deg, #00a81c 0%, #00c922 100%) !important;
  border: none !important;
  box-shadow: 0 4px 16px rgba(0, 168, 28, 0.3);
  transition: all 0.3s ease;
}

.rounded-pill.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 168, 28, 0.4);
}

.rounded-pill.btn-outline-success {
  border: 2px solid #00a81c !important;
  color: #00a81c !important;
  background: transparent !important;
  transition: all 0.3s ease;
}

.rounded-pill.btn-outline-success:hover {
  background: linear-gradient(135deg, #00a81c 0%, #00c922 100%) !important;
  color: #fff !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 168, 28, 0.3);
}

@media (max-width: 991.98px) {
  .policy-content {
    padding: 0 10px;
  }

  .alert-warning {
    padding: 16px 18px;
  }

  .warning-icon {
    font-size: 24px;
  }
}
</style>
